import { action, computed, makeObservable, observable, override } from 'mobx';
import moment from 'moment';
import {
	FormFieldType,
	IBaseApiModel,
	IContact,
	IFormBase,
	IFormField,
	IFormResponse,
	IOperationResult,
	IPageCollectionController,
	IUser,
	PageCollectionController,
	UserSessionContext,
	ViewModel,
	asApiError,
	getEmptyPageControllerResolvedPromise,
} from '../../extViewmodels';
import { FormFieldResolvers } from './FormFieldResolvers';
import { IMeme } from './gamification';
import { ILeadView } from './leads/interfaces';
import { AidaNoteViewModel } from './note';

export enum FieldKey {
	BadInfo = 'BadInfo',
	BadLead = 'BadLead',
	BDUapproved = 'BDUapproved',
	Booked = 'Booked',
	Captive = 'Captive',
	Connected = 'Connected',
	Disconnected = 'Disconnected',
	Duplicate = 'Duplicate',
	Enterprise = 'Enterprise',
	GKBlocked = 'GKBlocked',
	HookRejected = 'HookRejected',
	LeftVoiceMail = 'LeftVoiceMail',
	NoAnswer = 'NoAnswer',
	None = 'None',
	OffVertical = 'OffVertical',
	Other = 'Other',
	PermanentlyClosed = 'PermanentlyClosed',
	QualifiedPitch = 'QualifiedPitch',
	QualityPitch = 'QualityPitch',
	QuickPitch = 'QuickPitch',
	Retired = 'Retired',
	Shortcuts = 'Shortcuts',
	Stumped = 'Stumped',
	TopShortcuts = 'TopShortcuts',
	Vertical = 'Vertical',
	MeetingOutcomes = 'MeetingOutcomes',
	AppointmentConfirmed = 'AppointmentConfirmed',
	AppointmentConfirmationFailedAttempt = 'AppointmentConfirmationFailedAttempt',
	AppointmentCancelled = 'AppointmentCancelled',
	AppointmentRescheduled = 'AppointmentRescheduled',
	/** A "new" demo because the last one did not happen to be used for reschedule calls */
	NewDemo = 'NewDemo',

	/** FE only? */
	Unknown = 'Unknown',
}

export interface IFormFieldOption extends IBaseApiModel {
	name: FieldKey;
	label: string;
	description?: string;
	fieldId: string;
	hexColor?: string;
}

export interface IFormRequest {
	values: Record<string, string>;
	meetingId?: string;
}

type ICustomFormData = Record<string, any>;

export interface IForm extends IFormBase {
	fields: FormFieldViewModel<any>[];
}

export interface IFormFieldConfig {
	baseApiUrl: string;
	saveOnChange?: boolean;
}

export interface IInitiateMeetingRequest {
	companyId: string;
	contactId?: string;
	timeZone?: string;
}

export interface IInitiateSchedulerResponse {
	scheduledMeetingId: string;
	/** Full URL to launch to */
	url: string;
}

export class FormFieldViewModel<T> extends ViewModel {
	protected mField: IFormField<T> = null;
	protected mOptions: IFormFieldOption[] = [];
	protected mPerson: T = null;
	protected mTempValue: T;
	protected optionsPageCollectionController: IPageCollectionController<IFormFieldOption>;
	protected mSaveOnChange: boolean;
	protected mSchedulerLink: IInitiateSchedulerResponse;
	public note: AidaNoteViewModel;
	public validationError = '';
	public saving = false;

	private mBaseApiUrl: string;

	constructor(userSession: UserSessionContext, field: IFormField<T>, config: IFormFieldConfig) {
		super(userSession);

		makeObservable<
			FormFieldViewModel<T>,
			| 'mField'
			| 'mOptions'
			| 'mPerson'
			| 'mTempValue'
			| 'optionsPageCollectionController'
			| 'mSaveOnChange'
			| 'mSchedulerLink'
			| 'mSave'
		>(this, {
			mField: observable,
			mOptions: observable,
			mPerson: observable,
			mTempValue: observable,
			optionsPageCollectionController: observable,
			mSaveOnChange: observable,
			mSchedulerLink: observable,
			note: observable,
			validationError: observable,
			saving: observable,
			decimalPlaces: computed,
			fieldType: computed,
			id: computed,
			isOptional: computed,
			label: computed,
			maxLength: computed,
			name: computed,
			options: computed,
			person: computed,
			placeholder: computed,
			tempValue: computed,
			value: computed,
			schedulerLink: computed,
			setSchedulerLink: action,
			clearSchedulerLink: action,
			isHidden: computed,
			setHidden: action,
			load: override,
			reset: action,
			set: action,
			setPerson: action,
			validate: action,
			loadOptions: action,
			loadMeetingLink: action,
			mSave: action,
			updateField: action,
		});

		this.mBaseApiUrl = config.baseApiUrl;
		this.mSaveOnChange = !!config.saveOnChange;
		this.mField = field;

		this.optionsPageCollectionController = new PageCollectionController(
			this.userSession.webServiceHelper,
			`${this.mBaseApiUrl}/Field/${this.mField.id}`
		);

		if (this.fieldType === FormFieldType.Note) {
			// potentially initialize with the outcome note?
			this.note = new AidaNoteViewModel(userSession, {
				id: (this.mField.value as unknown as string) || '',
			});
		}
	}

	get decimalPlaces() {
		return this.mField.decimalPlaces;
	}

	get fieldType() {
		return this.mField.fieldType;
	}

	get id() {
		return this.mField.id;
	}

	get isOptional() {
		return this.mField.isOptional;
	}

	get label() {
		return this.mField.label;
	}

	get maxLength() {
		return this.mField.maxLength;
	}

	get name() {
		return this.mField.name;
	}

	get options() {
		return this.mOptions;
	}

	get person() {
		return this.mPerson;
	}

	get placeholder() {
		return this.mField.placeHolder;
	}

	get tempValue() {
		return this.mTempValue;
	}

	get value() {
		return this.mField.value;
	}

	get schedulerLink() {
		return this.mSchedulerLink;
	}

	public setSchedulerLink(meetingId: string) {
		this.mSchedulerLink = {
			scheduledMeetingId: meetingId,
			url: '',
		};
	}

	public clearSchedulerLink() {
		this.mSchedulerLink = null;
	}

	get isHidden() {
		return this.mField.isHidden;
	}

	public setHidden(hidden: boolean) {
		return (this.mField = {
			...this.mField,
			isHidden: hidden,
		});
	}

	/**
	 * Performs any necessary loading depding on the field type.
	 *
	 * @returns Promise<void>
	 */
	public load = async () => {
		switch (this.mField.fieldType) {
			case FormFieldType.Option:
			case FormFieldType.MultipleOptions:
				return this.loadOptions();
			case FormFieldType.Date:
			case FormFieldType.DateTime:
				this.validationError = 'Field Required';
				return;
			default:
				return;
		}
	};

	public reset = () => {
		this.busy = false;

		this.mTempValue = null;
		this.mOptions = [];

		this.mSchedulerLink = null;
	};

	public set = (val: any) => {
		this.validationError = '';
		this.mTempValue = val;

		if (this.mSaveOnChange) {
			this.mSave();
		}
	};

	public setPerson = (person: T) => {
		this.set(person);
		this.mPerson = person;
	};

	public validate = () => {
		// Do not validate hidden fields
		if (this.isHidden) {
			return;
		}

		if (!this.isOptional && !this.tempValue && !this.value) {
			this.validationError = `${this.label} is required`;
		}
	};

	public toJs() {
		return this.mField;
	}

	public loadOptions = async (search?: string) => {
		if (this.optionsPageCollectionController?.hasAllPages(null, null, { search })) {
			return getEmptyPageControllerResolvedPromise<IFormField<T>>();
		}

		this.busy = true;

		const promise = this.optionsPageCollectionController.getNext(null, null, {
			search,
		});
		promise
			.then(result => {
				this.mOptions = result.fetchedFirstPage
					? result.values
					: result.values.length > 0
						? [...this.mOptions, ...result.values]
						: this.mOptions;
				this.busy = false;
			})
			.catch(() => {
				this.mOptions = [];
				this.busy = false;
			});
		return promise;
	};

	public loadMeetingLink = async (request: IInitiateMeetingRequest): Promise<IInitiateSchedulerResponse> => {
		this.busy = true;
		// TODO find lib for this
		let url = `lead/${request.companyId}/meetingScheduler?`;
		if (request.contactId) {
			url = url.concat(`contactId=${encodeURIComponent(request.contactId)}`);
		}
		if (request.timeZone) {
			if (request.contactId) {
				url += '&';
			}
			url = url.concat(`timeZone=${encodeURIComponent(request.timeZone)}`);
		}
		const result = await this.userSession.webServiceHelper.callWebServiceAsync<IInitiateSchedulerResponse>(
			this.composeApiUrl({ urlPath: url }),
			'POST'
		);

		if (!result.success) {
			this.busy = false;
			throw result;
		}

		this.mSchedulerLink = result.value;
		this.busy = false;

		return result.value;
	};

	protected mSave = async () => {
		if (!this.saving) {
			this.saving = true;

			if (this.note) {
				await this.note.save(this.note.toJs());
			}

			this.saving = false;
		}
	};

	public updateField = (field: IFormField<T>) => {
		this.mField = field;
	};
}

/**
 * Right now the custom form view model is used exclusively for deals. In the future, it should be expanded so that it
 * can be used for any custom forms. Some minor refactoring may be needed, particularly around the base routes. On
 * initialization, load the form fields contained within the custom form
 */
export class CustomFormViewModel extends ViewModel {
	private mForm: IForm = null;
	public lead: ILeadView;
	public loadingForm = false;
	public saving = false;
	public saved = false;
	public customProperties: Record<string, any>;
	private mBaseApiUrl: string;

	constructor(userSession: UserSessionContext, lead: ILeadView, baseApiUrl?: string) {
		super(userSession);

		makeObservable<CustomFormViewModel, 'mForm'>(this, {
			mForm: observable,
			lead: observable,
			loadingForm: observable,
			saving: observable,
			saved: observable,
			customProperties: observable,
			companyId: computed,
			dealId: computed,
			form: computed,
			fields: computed,
			forceSet: action,
			loadForm: action,
			save: action,
			update: action,
		});

		this.lead = lead;

		this.mBaseApiUrl = baseApiUrl;
	}

	get companyId() {
		return this.lead.company?.id;
	}

	get dealId() {
		return this.mBaseApiUrl.toLowerCase().split('deal/')[1];
	}

	get form() {
		return this.mForm;
	}

	get fields() {
		return this.mForm?.fields;
	}

	public forceSet = (form: IForm) => {
		this.mForm = form;
	};

	public toJs() {
		return this.mForm;
	}

	public loadForm = async () => {
		if (!this.loadingForm) {
			this.loadingForm = true;

			try {
				const value = await this.userSession.webServiceHelper.callAsync<IFormResponse>(
					this.composeApiUrl({ urlPath: this.mBaseApiUrl }),
					'GET'
				);

				this.mForm = {
					...value,

					fields: value.fields.map(
						(field: IFormField<any>) =>
							new FormFieldViewModel<IFormFieldOption[]>(this.userSession, field, { baseApiUrl: this.mBaseApiUrl })
					),
				};
			} finally {
				this.loadingForm = false;
			}
		}
	};

	public save = async (customData: ICustomFormData = {}) => {
		if (this.saving) {
			return;
		}
		this.saving = true;
		const formData: IFormRequest = { values: {} };
		await Promise.all(
			this.mForm.fields.map(async f => {
				const field = { ...f.toJs() };
				if (!!f.tempValue && f.tempValue !== field.value) {
					field.value = f.tempValue;
				}
				if (field.fieldType === FormFieldType.Note) {
					try {
						await f.note.save(f.note.toJs());
					} catch (error) {
						this.saved = false;
						this.saving = false;
						throw asApiError(error);
					}
				}

				switch (field.fieldType) {
					case FormFieldType.User:
					case FormFieldType.Contact: {
						const value = (field.value as IFormFieldOption | IUser | IContact)?.id;
						if (value) {
							formData.values[field.id] = value;
						}
						break;
					}
					case FormFieldType.Note: {
						if (f.note?.id) {
							formData.values[field.id] = f.note.id;
						}
						break;
					}
					case FormFieldType.Date:
					case FormFieldType.DateTime: {
						formData.values[field.id] = moment(field.value as Date).format();
						break;
					}
					default: {
						formData.values[field.id] = field.value as string;
						break;
					}
				}
			})
		);

		// if there is a meeting id, use that,
		// otherwise send the demo perform date
		// but only one OR the other, not both
		const meetingSchedulerField = this.mForm.fields.find(
			x => x.name === FormFieldResolvers.getMeetingSchedulerField(this.userSession)
		);

		const demoPerformDateField = this.mForm.fields.find(
			x => x.name === FormFieldResolvers.getDemoPerformDateField(this.userSession)
		);

		// Only validate if demo perform date field is present.
		if (demoPerformDateField) {
			if (meetingSchedulerField?.schedulerLink?.scheduledMeetingId) {
				formData.meetingId = meetingSchedulerField.schedulerLink.scheduledMeetingId;

				delete formData.values[demoPerformDateField.id];
			} else if (demoPerformDateField.tempValue ?? demoPerformDateField.value) {
				formData.values[demoPerformDateField.id] = moment(
					demoPerformDateField.tempValue ?? demoPerformDateField.value
				).format();
				delete formData.meetingId;
			} else {
				this.saved = false;
				this.saving = false;
				throw asApiError(new Error('You must book a meeting or provide a demo perform date'));
			}
		}

		const body = {
			...formData,
			...customData,
			customProperties: this.customProperties,
		};
		const result: IOperationResult<{ meme: IMeme }> = this.dealId
			? await this.userSession.webServiceHelper.callWebServiceAsync(
					this.composeApiUrl({ urlPath: this.mBaseApiUrl }),
					'PUT',
					body
				)
			: await this.userSession.webServiceHelper.callWebServiceAsync<{
					meme: IMeme;
				}>(this.composeApiUrl({ urlPath: this.mBaseApiUrl }), 'POST', body);
		if (!result.success) {
			this.saving = false;
			throw asApiError(result);
		}

		this.saving = false;
		this.saved = true;

		return result.value?.meme || null;
	};

	public update = (form: IFormResponse, baseUrl: string) => {
		this.mForm = {
			...form,
			fields: this.mapFields(form.fields, baseUrl),
		};
	};

	private mapFields = (fields: IFormField<any>[], baseUrl: string) => {
		return fields.map(field => {
			const existingField = this.fields?.find(f => f.id === field.id);

			if (existingField) {
				existingField.updateField(field);
				return existingField;
			} else {
				return new FormFieldViewModel(this.userSession, field, {
					baseApiUrl: baseUrl,
					saveOnChange: true,
				});
			}
		});
	};
}
